import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { Currency } from 'types';
import { formatAmount } from 'utils/helpers';

interface EditQuotedOfferModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (quotedOffer: number) => Promise<void>;
    currentOffer: number;
    influencerName?: string;
    currency: Currency;
}

const EditQuotedOfferModal: React.FC<EditQuotedOfferModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    currentOffer,
    influencerName,
    currency,
}) => {
    const [value, setValue] = useState(String(currentOffer));
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setValue(String(currentOffer));
            setError(null);
            setSubmitting(false);
        }
    }, [isOpen, currentOffer]);

    const parsed = Number(value);
    const isUnchanged = !isNaN(parsed) && parsed === currentOffer;
    const canSubmit = value.trim() !== '' && !isNaN(parsed) && parsed > 0 && !isUnchanged && !submitting;

    const handleSubmit = async () => {
        if (value.trim() === '' || isNaN(parsed) || parsed <= 0) {
            setError('Enter a valid amount greater than 0');
            return;
        }
        if (isUnchanged) {
            setError('Update the amount before submitting');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            await onSubmit(parsed);
            onClose();
        } catch {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            centered
            isOpen={isOpen}
            toggle={onClose}
            className="edit-quoted-offer-modal"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
            <ModalHeader toggle={onClose} className="edit-quoted-offer-modal__header py-3">
                <span className="edit-quoted-offer-modal__title">Edit quoted offer</span>
                {influencerName && (
                    <span className="edit-quoted-offer-modal__subtitle">{influencerName}</span>
                )}
            </ModalHeader>
            <ModalBody className="edit-quoted-offer-modal__body">
                <p className="edit-quoted-offer-modal__hint">
                    Current offer · <strong>{formatAmount(currentOffer, currency?.code, currency?.symbol, currency?.locale)}</strong>
                </p>
                <div className="edit-quoted-offer-modal__field-block">
                    <label className="edit-quoted-offer-modal__label" htmlFor="quoted-offer-input">
                        New amount
                    </label>
                    <div className="edit-quoted-offer-modal__input-wrap">
                        <span className="edit-quoted-offer-modal__currency" aria-hidden>{currency?.symbol}</span>
                        <input
                            id="quoted-offer-input"
                            type="number"
                            min={1}
                            step={1}
                            autoFocus
                            className="edit-quoted-offer-modal__input"
                            value={value}
                            disabled={submitting}
                            onChange={(e) => {
                                setValue(e.target.value);
                                if (error) setError(null);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && canSubmit) {
                                    e.preventDefault();
                                    handleSubmit();
                                }
                            }}
                        />
                    </div>
                    {error && <span className="edit-quoted-offer-modal__error">{error}</span>}
                </div>
            </ModalBody>
            <ModalFooter className="edit-quoted-offer-modal__footer py-2">
                <button
                    type="button"
                    className="theme-btn edit-quoted-offer-modal__cancel"
                    onClick={onClose}
                    disabled={submitting}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="theme-btn btn-gradient edit-quoted-offer-modal__submit"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                >
                    {submitting ? <Spinner size="sm" color="light" /> : 'Save offer'}
                </button>
            </ModalFooter>
        </Modal>
    );
};

export default EditQuotedOfferModal;
